
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useToast } from "@/hooks/use-toast";
import { questions } from '@/data/questions';
import { Topic } from '@/components/TopicSelector';
import { topics } from '@/data/topics';

interface Question {
  id: string;
  text: string;
  type: 'single' | 'multiple' | 'true-false';
  options: {
    id: string;
    text: string;
    isCorrect: boolean;
  }[];
  explanation?: string;
  topic: string;
}

interface QuizContextType {
  currentQuestion: Question | null;
  currentQuestionIndex: number;
  topics: Topic[];
  currentTopic: string | null;
  questionsByTopic: Record<string, Question[]>;
  correctAnswers: number;
  isComplete: boolean;
  topicQuestions: Question[];
  setCurrentTopic: (topicId: string) => void;
  nextQuestion: () => void;
  handleAnswer: (correct: boolean) => void;
  resetQuiz: () => void;
}

const QuizContext = createContext<QuizContextType | undefined>(undefined);

export const QuizProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const [currentTopic, setCurrentTopic] = useState<string | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [questionsByTopic, setQuestionsByTopic] = useState<Record<string, Question[]>>({});
  const [topicQuestions, setTopicQuestions] = useState<Question[]>([]);
  const { toast } = useToast();

  // Shuffle an array using Fisher-Yates algorithm
  const shuffleArray = <T,>(array: T[]): T[] => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  // Randomize the options within a question
  const randomizeQuestionOptions = (question: Question): Question => {
    return {
      ...question,
      options: shuffleArray(question.options)
    };
  };

  // Group questions by topic
  useEffect(() => {
    const groupedQuestions: Record<string, Question[]> = {};
    
    questions.forEach(question => {
      if (!groupedQuestions[question.topic]) {
        groupedQuestions[question.topic] = [];
      }
      groupedQuestions[question.topic].push(question as Question);
    });
    
    setQuestionsByTopic(groupedQuestions);
  }, []);

  // Set topic questions when a topic is selected
  useEffect(() => {
    if (currentTopic && questionsByTopic[currentTopic]) {
      // Get all questions for the selected topic
      const allQuestions = questionsByTopic[currentTopic];
      
      // Find the expected question count from the topics array
      const selectedTopic = topics.find(t => t.id === currentTopic);
      const expectedQuestionCount = selectedTopic?.questionCount || allQuestions.length;
      
      // Shuffle all questions and randomize their options
      const randomizedQuestions = shuffleArray(allQuestions)
        .map(question => randomizeQuestionOptions(question))
        // Limit to the expected question count
        .slice(0, expectedQuestionCount);
      
      // Use limited question set
      setTopicQuestions(randomizedQuestions);
      setCurrentQuestionIndex(0);
      setCorrectAnswers(0);
      setIsComplete(false);
    }
  }, [currentTopic, questionsByTopic]);

  const currentQuestion = topicQuestions[currentQuestionIndex] || null;

  const handleTopicSelect = (topicId: string) => {
    setCurrentTopic(topicId);
    toast({
      title: "Topic selected",
      description: `Starting quiz on ${topics.find(t => t.id === topicId)?.title}`,
    });
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < topicQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      setIsComplete(true);
    }
  };

  const handleAnswer = (correct: boolean) => {
    if (correct) {
      setCorrectAnswers(prev => prev + 1);
    }
  };

  const resetQuiz = () => {
    setCurrentQuestionIndex(0);
    setCorrectAnswers(0);
    setIsComplete(false);
    
    // Reshuffle all questions and randomize their options
    if (currentTopic && questionsByTopic[currentTopic]) {
      const allQuestions = questionsByTopic[currentTopic];
      const selectedTopic = topics.find(t => t.id === currentTopic);
      const expectedQuestionCount = selectedTopic?.questionCount || allQuestions.length;
      
      const randomizedQuestions = shuffleArray(allQuestions)
        .map(question => randomizeQuestionOptions(question))
        .slice(0, expectedQuestionCount);
      
      setTopicQuestions(randomizedQuestions);
    }
  };

  const value = {
    currentQuestion,
    currentQuestionIndex,
    topics,
    currentTopic,
    questionsByTopic,
    correctAnswers,
    isComplete,
    topicQuestions,
    setCurrentTopic: handleTopicSelect,
    nextQuestion,
    handleAnswer,
    resetQuiz,
  };

  return (
    <QuizContext.Provider value={value}>
      {children}
    </QuizContext.Provider>
  );
};

export const useQuiz = () => {
  const context = useContext(QuizContext);
  if (context === undefined) {
    throw new Error('useQuiz must be used within a QuizProvider');
  }
  return context;
};
